import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { useListProductsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { useExportSalesCsvMutation } from '../services/dashboard.endpoints';
import SalesInsightsCharts from './SalesInsightsCharts';
import {
  borders,
  brand,
  dashboardSalesStyles,
  panelSectionSx,
  scopeSectionSx,
} from '../dashboardSales.styles';

const defaultChecked = {
  address: false,
  zipAndCity: false,
  totalPaid: true,
  dateOrdered: false,
  productId: false,
  quantity: true,
  productInfo: true,
  cartId: false,
  saleType: true,
  flatten: false,
  includeGrandTotal: true,
  includeProductTotals: true,
  includeHostAppTotals: true,
  includePlatformTotals: true,
  includeChurnSummary: true,
};

type FieldKey = keyof typeof defaultChecked;

type ToggleField = {
  key: FieldKey;
  label: string;
  hint?: string;
};

const DATE_PRESETS: { label: string; from: () => Dayjs; to: () => Dayjs }[] = [
  {
    label: '30 days',
    from: () => dayjs().subtract(30, 'day'),
    to: () => dayjs(),
  },
  {
    label: '90 days',
    from: () => dayjs().subtract(90, 'day'),
    to: () => dayjs(),
  },
  {
    label: 'YTD',
    from: () => dayjs().startOf('year'),
    to: () => dayjs(),
  },
  {
    label: 'Last year',
    from: () => dayjs().subtract(1, 'year').startOf('year'),
    to: () => dayjs().subtract(1, 'year').endOf('year'),
  },
];

const COLUMN_GROUPS: { title: string; fields: ToggleField[] }[] = [
  {
    title: 'Customer',
    fields: [
      { key: 'address', label: 'Address' },
      { key: 'zipAndCity', label: 'Zip & city' },
    ],
  },
  {
    title: 'Order',
    fields: [
      { key: 'dateOrdered', label: 'Date ordered' },
      { key: 'totalPaid', label: 'Total paid' },
      { key: 'cartId', label: 'Cart ID' },
      { key: 'flatten', label: 'One row per cart', hint: 'Merge line items' },
    ],
  },
  {
    title: 'Product',
    fields: [
      { key: 'productInfo', label: 'Product name(s)' },
      { key: 'productId', label: 'Product ID' },
      { key: 'quantity', label: 'Quantity' },
    ],
  },
  {
    title: 'Subscription',
    fields: [{ key: 'saleType', label: 'Sale type' }],
  },
];

const SUMMARY_FIELDS: ToggleField[] = [
  { key: 'includeGrandTotal', label: 'Grand total row' },
  { key: 'includeProductTotals', label: 'Totals by product' },
  { key: 'includeHostAppTotals', label: 'Sales by host app' },
  { key: 'includePlatformTotals', label: 'Sales by operating system' },
];

const checkboxRowSx = {
  ml: 0,
  mr: 0,
  py: 0.25,
  alignItems: 'center',
  '& .MuiCheckbox-root': { p: 0.75 },
  '& .MuiFormControlLabel-label': { fontSize: '0.875rem', lineHeight: 1.43 },
} as const;

const columnActionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  borderColor: borders.medium,
  color: brand.slate,
  px: 1.75,
  py: 0.5,
  minWidth: 108,
  boxShadow: '0 1px 2px rgba(55, 71, 79, 0.08)',
  '&:hover': {
    borderColor: brand.slate,
    bgcolor: 'rgba(55, 71, 79, 0.06)',
  },
} as const;

const dateFieldSlotProps = {
  textField: { size: 'small' as const, fullWidth: true },
};

function ColumnCheckbox({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox size="small" checked={checked} onChange={onChange} />
        }
        label={label}
        sx={checkboxRowSx}
      />
      {hint ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            pl: 4.25,
            mt: -0.5,
            mb: 0.5,
            lineHeight: 1.3,
          }}
        >
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

function DateRangePair({
  from,
  to,
  onFromChange,
  onToChange,
  fromLabel = 'From',
  toLabel = 'To',
}: {
  from: Dayjs | null;
  to: Dayjs | null;
  onFromChange: (d: Dayjs | null) => void;
  onToChange: (d: Dayjs | null) => void;
  fromLabel?: string;
  toLabel?: string;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ sm: 'center' }}
    >
      <DatePicker
        label={fromLabel}
        value={from}
        onChange={onFromChange}
        maxDate={to ?? undefined}
        slotProps={dateFieldSlotProps}
        sx={{ flex: 1, minWidth: { sm: 140 } }}
      />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ display: { xs: 'none', sm: 'block' }, px: 0.5 }}
      >
        –
      </Typography>
      <DatePicker
        label={toLabel}
        value={to}
        onChange={onToChange}
        minDate={from ?? undefined}
        slotProps={dateFieldSlotProps}
        sx={{ flex: 1, minWidth: { sm: 140 } }}
      />
    </Stack>
  );
}

const panelIconSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 1.5,
  flexShrink: 0,
  border: '1px solid',
  borderColor: borders.light,
  bgcolor: 'rgba(55, 71, 79, 0.06)',
  color: brand.slate,
} as const;

function PanelSection({
  icon,
  title,
  subtitle,
  accentColor = brand.slate,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accentColor?: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={panelSectionSx(accentColor)}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={panelIconSx}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} lineHeight={1.4}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.25, mb: 1.5 }}
            >
              {subtitle}
            </Typography>
          ) : (
            <Box sx={{ mb: 1.5 }} />
          )}
          {children}
        </Box>
      </Stack>
    </Box>
  );
}

const SalesExportForm = () => {
  const [exportSalesCsv, { isLoading }] = useExportSalesCsvMutation();
  const { data: allProducts = [] } = useListProductsQuery({
    includeHidden: true,
  });
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    dayjs().subtract(30, 'day'),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
  const [fileName, setFileName] = useState('sales-export');
  const [fields, setFields] =
    useState<Record<FieldKey, boolean>>(defaultChecked);

  const handleFieldToggle = (key: FieldKey) => {
    setFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setGroupFields = (keys: FieldKey[], checked: boolean) => {
    setFields((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = checked;
      });
      return next;
    });
  };

  const optionalColumnKeys = COLUMN_GROUPS.flatMap((g) =>
    g.fields.map((f) => f.key),
  );

  const applyPreset = (preset: (typeof DATE_PRESETS)[number]) => {
    setFromDate(preset.from());
    setToDate(preset.to());
  };

  const chartFilters = {
    from: fromDate,
    to: toDate,
    productIds: selectedProductIds,
  };

  const periodSummary =
    fromDate && toDate
      ? `${fromDate.format('MMM D, YYYY')} – ${toDate.format('MMM D, YYYY')}`
      : 'Select a date range';

  const exportReady = !!fromDate && !!toDate;

  const handleExport = async () => {
    if (!exportReady) return;

    const exportParams = {
      from: fromDate!.format('YYYY-MM-DD'),
      to: toDate!.format('YYYY-MM-DD'),
      fileName,
      fields: Object.entries(fields)
        .filter(([k, v]) => v && !k.startsWith('include'))
        .map(([k]) => k),
      productIds: selectedProductIds,
      includeGrandTotal: fields.includeGrandTotal,
      includeProductTotals: fields.includeProductTotals,
      includeHostAppTotals: fields.includeHostAppTotals,
      includePlatformTotals: fields.includePlatformTotals,
      includeChurnSummary: fields.includeChurnSummary,
    };

    const result = await exportSalesCsv(exportParams);
    if (!('data' in result) || !result.data) {
      return;
    }

    const blob = result.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Paper elevation={0} sx={dashboardSalesStyles.mainCard}>
      <Box sx={dashboardSalesStyles.header}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: 'inherit' }}>
              Sales report & export
            </Typography>
            <Typography
              variant="body2"
              sx={dashboardSalesStyles.headerSubtitle}
            >
              Charts and CSV use the same filters below.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            disableElevation
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={isLoading || !exportReady}
            sx={{
              ...dashboardSalesStyles.exportButton,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            {isLoading ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Stack>
      </Box>

      <Box sx={dashboardSalesStyles.filtersPanel}>
        <Stack spacing={2.5}>
          <PanelSection
            icon={<CalendarMonthOutlinedIcon fontSize="small" />}
            title="Report period"
            subtitle={`Drives charts and CSV detail rows · ${periodSummary}`}
            accentColor={brand.slateLight}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              alignItems={{ lg: 'flex-end' }}
            >
              <Box sx={{ flex: 1, maxWidth: { lg: 420 } }}>
                <DateRangePair
                  from={fromDate}
                  to={toDate}
                  onFromChange={setFromDate}
                  onToChange={setToDate}
                />
              </Box>

              <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                {DATE_PRESETS.map((preset) => (
                  <Chip
                    key={preset.label}
                    label={preset.label}
                    size="small"
                    variant="outlined"
                    onClick={() => applyPreset(preset)}
                    sx={dashboardSalesStyles.presetChip}
                  />
                ))}
              </Stack>
            </Stack>
          </PanelSection>

          <Box sx={scopeSectionSx()}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.75 }}
                >
                  Products
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={allProducts}
                  getOptionLabel={(option) => option.name}
                  value={allProducts.filter((p) =>
                    selectedProductIds.includes(p._id),
                  )}
                  onChange={(_, newValue) =>
                    setSelectedProductIds(newValue.map((p) => p._id))
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        label={option.name}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        selectedProductIds.length === 0
                          ? 'All products'
                          : 'Add product…'
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.75 }}
                >
                  Export file name
                </Typography>
                <TextField
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="sales-export"
                />
              </Grid>
            </Grid>
          </Box>

          <PanelSection
            icon={<TableChartOutlinedIcon fontSize="small" />}
            title="CSV options"
            subtitle="Columns and summary rows for export · name and email are always included"
            accentColor={brand.teal}
          >
            <Stack spacing={2}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', letterSpacing: 0.8 }}
              >
                Columns
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Chip
                  label="Last name"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(55, 71, 79, 0.06)',
                    borderColor: 'rgba(55, 71, 79, 0.2)',
                  }}
                />
                <Chip
                  label="First name"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(55, 71, 79, 0.06)',
                    borderColor: 'rgba(55, 71, 79, 0.2)',
                  }}
                />
                <Chip
                  label="Email"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(55, 71, 79, 0.06)',
                    borderColor: 'rgba(55, 71, 79, 0.2)',
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setGroupFields(optionalColumnKeys, true)}
                  sx={columnActionButtonSx}
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setGroupFields(optionalColumnKeys, false)}
                  sx={columnActionButtonSx}
                >
                  Clear optional
                </Button>
              </Stack>

              <Grid container spacing={2}>
                {COLUMN_GROUPS.map((group) => (
                  <Grid item xs={12} sm={6} md={3} key={group.title}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5, letterSpacing: 0.8 }}
                    >
                      {group.title}
                    </Typography>
                    <FormGroup sx={{ gap: 0.25 }}>
                      {group.fields.map(({ key, label, hint }) => (
                        <ColumnCheckbox
                          key={key}
                          label={label}
                          hint={hint}
                          checked={fields[key]}
                          onChange={() => handleFieldToggle(key)}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: borders.medium,
                  pt: 2,
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 0.8 }}
                >
                  Summary rows
                </Typography>
                <FormGroup row sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                  {SUMMARY_FIELDS.map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          size="small"
                          checked={fields[key]}
                          onChange={() => handleFieldToggle(key)}
                        />
                      }
                      label={label}
                      sx={checkboxRowSx}
                    />
                  ))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={fields.includeChurnSummary}
                        onChange={() =>
                          handleFieldToggle('includeChurnSummary')
                        }
                      />
                    }
                    label="Renewal rate summary"
                    sx={checkboxRowSx}
                  />
                </FormGroup>

                {fields.includeChurnSummary && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Compares first-time subscription sales from one year before
                    the report period to renewals in the report period.
                  </Typography>
                )}
              </Box>
            </Stack>
          </PanelSection>

          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              pt: 0.5,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              size="large"
              disableElevation
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={isLoading || !exportReady}
              sx={{
                bgcolor: brand.slate,
                '&:hover': { bgcolor: brand.slateLight },
              }}
            >
              {isLoading ? 'Exporting…' : 'Export CSV'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Box sx={dashboardSalesStyles.chartsSection}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{
            color: brand.slate,
            letterSpacing: '0.02em',
            mb: 1.5,
            display: 'block',
          }}
        >
          Overview
        </Typography>
        <SalesInsightsCharts filters={chartFilters} />
      </Box>
    </Paper>
  );
};

export default SalesExportForm;
